import { useCallback, useEffect, useRef, useState, type CSSProperties, type KeyboardEvent as RKeyEvent, type PointerEvent as RPointerEvent } from 'react';
import { Link } from '@tanstack/react-router';
import { IconArrowsMaximize, IconSend, IconX } from '@tabler/icons-react';
import { AppIcon } from '@hana/ui';
import s from './shell.module.css';
import { useT } from '@hana/i18n';

const KEY = 'mint.dock.v1';
const HALF = 28; // nửa bóng 56
const MG = 10; // lề kẹp mép (MDK.mg)
const STEP = 16; // phím mũi tên · Shift = 64 (MDK.step / stepL)
const STEP_L = 64;
/** Vị trí neo theo cạnh gần nhất: khoảng cách từ cạnh tới TÂM bóng → đổi khổ màn vẫn bám đúng phía (board MINT DOCK KIT). */
type Pos = { h: 'l' | 'r'; x: number; v: 't' | 'b'; y: number };
const okPos = (p: unknown): p is Pos => {
  const q = p as Pos | null;
  return !!q && typeof q === 'object' && (q.h === 'l' || q.h === 'r') && (q.v === 't' || q.v === 'b') && Number.isFinite(q.x) && Number.isFinite(q.y);
};
function loadPos(): Pos | null {
  try {
    const j = JSON.parse(localStorage.getItem(KEY) || 'null') as { pos?: unknown } | null;
    return j && okPos(j.pos) ? j.pos : null;
  } catch {
    return null;
  }
}
function savePos(pos: Pos | null) {
  try {
    const j = (JSON.parse(localStorage.getItem(KEY) || 'null') as Record<string, unknown> | null) ?? {};
    if (pos) j.pos = pos;
    else delete j.pos;
    localStorage.setItem(KEY, JSON.stringify(j));
  } catch {
    /* chỉ là tiện ích */
  }
}
/** Tâm bóng (px) từ vị trí neo, kẹp trong mép; thanh trên điện thoại / tab dưới không bị đè. */
function centre(p: Pos, w: number, h: number, hdr: number, tab: number): [number, number] {
  let x = p.h === 'l' ? p.x : w - p.x;
  let y = p.v === 't' ? p.y : h - p.y;
  x = Math.min(Math.max(x, MG + HALF), w - MG - HALF);
  y = Math.min(Math.max(y, MG + HALF), h - MG - HALF);
  if (hdr && y - HALF < hdr) y = hdr + 8 + HALF;
  if (tab && y + HALF > h - tab) y = h - tab - 8 - HALF;
  return [x, y];
}
function toPos(c: [number, number], w: number, h: number): Pos {
  const x = Math.round(c[0]);
  const y = Math.round(c[1]);
  const hh = x < w / 2 ? 'l' : 'r';
  const vv = y < h / 2 ? 't' : 'b';
  return { h: hh, x: hh === 'l' ? x : w - x, v: vv, y: vv === 't' ? y : h - y };
}

/** Dock Ask HANA (MINT DOCK KIT v1) — bản vỏ: bóng cú góc phải dưới + khung hỏi 420. Chưa nối AI. */
export function AskDock({ context, user }: { context: string; user: string }) {
  const t = useT();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [pos, setPos] = useState<Pos | null>(() => loadPos());
  const [vp, setVp] = useState<[number, number]>(() => [window.innerWidth, window.innerHeight]);
  const [drag, setDrag] = useState<[number, number] | null>(null);
  const d = useRef<{ x: number; y: number; c: [number, number]; moved: boolean } | null>(null);
  const justDragged = useRef(0);
  const phone = vp[0] < 700;
  const hdr = phone ? 56 : 0;
  const tab = phone ? 76 : 0;
  useEffect(() => {
    const r = () => setVp([window.innerWidth, window.innerHeight]);
    window.addEventListener('resize', r);
    return () => window.removeEventListener('resize', r);
  }, []);
  const cur = pos ? centre(pos, vp[0], vp[1], hdr, tab) : null;
  const place = useCallback(
    (c: [number, number]) => {
      const clamped = centre(toPos(c, vp[0], vp[1]), vp[0], vp[1], hdr, tab);
      const p = toPos(clamped, vp[0], vp[1]);
      setPos(p);
      savePos(p);
      setDrag(null);
      setOpen(false);
    },
    [vp, hdr, tab],
  );
  // bóng đang đứng ở đâu (kể cả vị trí mặc định theo CSS) — lấy từ DOM để kéo / phím bắt đầu đúng chỗ
  const fabRef = useRef<HTMLButtonElement>(null);
  const centreNow = (): [number, number] => {
    if (cur) return cur;
    const r = fabRef.current?.getBoundingClientRect();
    return r ? [r.left + r.width / 2, r.top + r.height / 2] : [vp[0] - 52, vp[1] - 52];
  };
  const onDown = (e: RPointerEvent<HTMLButtonElement>) => {
    if (e.button !== 0) return;
    d.current = { x: e.clientX, y: e.clientY, c: centreNow(), moved: false };
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch { /* vẫn kéo được */ }
  };
  const onMove = (e: RPointerEvent<HTMLButtonElement>) => {
    const k = d.current;
    if (!k) return;
    const dx = e.clientX - k.x;
    const dy = e.clientY - k.y;
    if (!k.moved && Math.hypot(dx, dy) < 6) return;
    k.moved = true;
    setOpen(false);
    setDrag([dx, dy]);
  };
  const onUp = (e: RPointerEvent<HTMLButtonElement>) => {
    const k = d.current;
    d.current = null;
    if (!k || !k.moved) return;
    justDragged.current = Date.now();
    place([k.c[0] + (e.clientX - k.x), k.c[1] + (e.clientY - k.y)]);
  };
  const onKey = (e: RKeyEvent<HTMLButtonElement>) => {
    const v = ({ ArrowLeft: [-1, 0], ArrowRight: [1, 0], ArrowUp: [0, -1], ArrowDown: [0, 1] } as Record<string, [number, number]>)[e.key];
    if (!v) return;
    e.preventDefault();
    const c = centreNow();
    const st = e.shiftKey ? STEP_L : STEP;
    place([c[0] + v[0] * st, c[1] + v[1] * st]);
  };
  // khung hỏi mở cạnh bóng, phía còn chỗ, nằm trọn trong khung (board: PW 420)
  const askStyle: CSSProperties | undefined = (() => {
    if (!cur || phone) return undefined;
    const PW = 420;
    const PH = Math.max(320, Math.min(640, vp[1] - 24));
    const right = cur[0] >= vp[0] / 2;
    const low = cur[1] >= vp[1] / 2;
    const left = Math.min(Math.max(right ? cur[0] - HALF - 12 - PW : cur[0] + HALF + 12, 12), vp[0] - 12 - PW);
    const top = Math.min(Math.max(low ? cur[1] + HALF - PH : cur[1] - HALF, 8), vp[1] - 12 - PH);
    return { left, top, right: 'auto', bottom: 'auto', height: PH };
  })();
  const fabStyle: CSSProperties | undefined = (() => {
    if (!cur && !drag) return undefined;
    const c = cur ?? centreNow();
    return { left: c[0] - HALF, top: c[1] - HALF, right: 'auto', bottom: 'auto', touchAction: 'none', ...(drag ? { transform: `translate(${drag[0]}px, ${drag[1]}px) scale(1.1) rotate(-6deg)`, cursor: 'grabbing' } : null) };
  })();
  useEffect(() => {
    if (!open) return;
    const k = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    window.addEventListener('keydown', k);
    return () => window.removeEventListener('keydown', k);
  }, [open]);
  // Ctrl+Alt+D mở / đóng Ask HANA (phím tắt của bóng theo board)
  useEffect(() => {
    const k = (e: KeyboardEvent) => {
      if (e.ctrlKey && e.altKey && (e.code === 'KeyD' || e.key.toLowerCase() === 'd')) {
        e.preventDefault();
        setOpen((o) => !o);
      }
    };
    window.addEventListener('keydown', k);
    return () => window.removeEventListener('keydown', k);
  }, []);
  return (
    <>
      {open && (
        <aside className={s.ask} style={askStyle} aria-label="Ask HANA">
          <div className={s.askHead}>
            <AppIcon app="ask" size={36} />
            <div>
              <b>Ask HANA</b>
              <small>{user}</small>
            </div>
            <Link to="/ask" className={s.ib} aria-label={t('shell.askFull')} title={t('shell.openFull')}>
              <IconArrowsMaximize size={16} />
            </Link>
            <button type="button" className={s.ib} aria-label={t('shell.askClose')} onClick={() => setOpen(false)}>
              <IconX size={16} />
            </button>
          </div>
          <div className={s.askCtx}>
            <span>{t('shell.context')}</span>
            <span>{context}</span>
          </div>
          <div className={s.askBody}>
            <AppIcon app="ask" size={64} />
            <b>{t('shell.askSoon')}</b>
            <span>{t('shell.askSoonDesc')}</span>
          </div>
          <div className={s.askFoot}>
            <div className={s.askIn}>
              <input aria-label={t('shell.askInput')} placeholder={t('shell.askPh')} value={q} onChange={(e) => setQ(e.target.value)} />
              <button type="button" className={s.askSend} aria-label={t('shell.askSend')} disabled title={t('shell.askOff')}>
                <IconSend size={15} />
              </button>
            </div>
            <div className={s.askNote}>{t('shell.askNote')}</div>
          </div>
        </aside>
      )}
      <button
        ref={fabRef}
        type="button"
        className={s.fab}
        style={fabStyle}
        aria-label={open ? t('shell.askClose') : t('shell.askFabLabel')}
        title="Ask HANA"
        aria-expanded={open}
        onClick={() => Date.now() - justDragged.current > 250 && setOpen(!open)}
        onPointerDown={onDown}
        onPointerMove={onMove}
        onPointerUp={onUp}
        onKeyDown={onKey}
      >
        {open ? <IconX size={22} stroke={2.2} /> : <AppIcon app="ask" size={56} />}
      </button>
    </>
  );
}
